import { DashboardLayout } from '@/components/layout/DashboardLayout';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useProject } from '@/contexts/ProjectContext';
import { useTheme } from '@/contexts/ThemeContext';
import { PermissionGate } from '@/components/PermissionGate';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Wrench, ArrowLeft, ArrowUpRight, ArrowDownLeft, Settings, RefreshCw,
  BarChart3, FileSpreadsheet, Search, Loader2, AlertTriangle, CheckCircle2,
  Clock, TrendingUp, Plus, X, Grid3X3, List, RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';
import DCIMatrixGrid from '@/components/engineering/DCIMatrixGrid';
import DCIRegisterList from '@/components/engineering/DCIRegisterList';
import StageGateConfig from '@/components/engineering/StageGateConfig';
import ExcelImportExport from '@/components/engineering/ExcelImportExport';
import GuidedImportWizard from '@/components/shared/GuidedImportWizard';
import { ENGINEERING_DCI_CONFIG } from '@/components/shared/importFieldConfigs';
import ModuleHelp from '@/components/common/ModuleHelp';
import CognaraMacroLinkModal from '@/components/construction/CognaraMacroLinkModal';
import { Sparkles } from 'lucide-react';

function KPICard({ title, value, subtitle, icon: Icon, color = '#1E4E8C' }) {
  return (
    <div className="rounded-xl p-4" data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}
      style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      <div className="flex items-start justify-between mb-2">
        <div className="p-2 rounded-lg" style={{ background: `${color}12`, border: `1px solid ${color}20` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <div className="text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{value}</div>
      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{title}</div>
      {subtitle && <div className="text-[10px] mt-1" style={{ color: 'var(--text-disabled)' }}>{subtitle}</div>}
    </div>
  );
}

function AddEntryDialog({ projectId, documentTypes, onClose, onCreated }) {
  const [form, setForm] = useState({ document_number: '', document_title: '', document_type_id: '', discipline: '', area: '', contractor: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.document_number || !form.document_title || !form.document_type_id || !form.discipline) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/engineering-schedule/entries`, form);
      toast.success('Entry created successfully');
      onCreated();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create entry');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm" style={{ backgroundColor: 'var(--overlay-bg)' }}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6"
        style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
        data-testid="add-entry-dialog">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Add DCI Entry</h3>
          <button onClick={onClose} style={{ color: 'var(--text-muted)' }}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase mb-1 block" style={{ color: 'var(--text-disabled)' }}>Document Number *</label>
              <Input data-testid="entry-doc-number" value={form.document_number}
                onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                placeholder="e.g. PRJ-PFD-001"
                style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>
            <div>
              <label className="text-[10px] uppercase mb-1 block" style={{ color: 'var(--text-disabled)' }}>Document Type *</label>
              <select data-testid="entry-doc-type" value={form.document_type_id}
                onChange={(e) => setForm({ ...form, document_type_id: e.target.value })}
                className="w-full px-3 py-2 rounded-md text-sm"
                style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                <option value="">Select type</option>
                {documentTypes.map((dt) => <option key={dt.id} value={dt.id}>{dt.code} - {dt.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase mb-1 block" style={{ color: 'var(--text-disabled)' }}>Document Title *</label>
            <Input data-testid="entry-doc-title" value={form.document_title}
              onChange={(e) => setForm({ ...form, document_title: e.target.value })}
              placeholder="e.g. Process Flow Diagram - Unit 100"
              style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase mb-1 block" style={{ color: 'var(--text-disabled)' }}>Discipline *</label>
              <Input data-testid="entry-discipline" value={form.discipline}
                onChange={(e) => setForm({ ...form, discipline: e.target.value })} placeholder="e.g. Process"
                style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>
            <div>
              <label className="text-[10px] uppercase mb-1 block" style={{ color: 'var(--text-disabled)' }}>Area</label>
              <Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="e.g. Unit 100"
                style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>
            <div>
              <label className="text-[10px] uppercase mb-1 block" style={{ color: 'var(--text-disabled)' }}>Contractor</label>
              <Input value={form.contractor} onChange={(e) => setForm({ ...form, contractor: e.target.value })} placeholder="e.g. ABC Engineering"
                style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="ghost" onClick={onClose} style={{ color: 'var(--text-muted)' }}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#1E4E8C] hover:bg-[#2850EE] text-white" data-testid="save-entry-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Create Entry
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

export default function EngineeringSchedule() {
  const navigate = useNavigate();
  const { selectedProject, projectId: ctxProjectId } = useProject();
  const { isDark } = useTheme();
  const activeProjectId = selectedProject?.project_id;

  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [stageGateConfigs, setStageGateConfigs] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('register'); // 'register' or 'matrix'
  const [showConfig, setShowConfig] = useState(false);
  const [showExcelDialog, setShowExcelDialog] = useState(false);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showAutoLink, setShowAutoLink] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const [pendingRollups, setPendingRollups] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [entriesRes, summaryRes, docTypesRes, configsRes] = await Promise.all([
        api.get(`/projects/${activeProjectId}/engineering-schedule/entries`),
        api.get(`/projects/${activeProjectId}/engineering-schedule/summary`),
        api.get(`/projects/${activeProjectId}/engineering-schedule/document-types`),
        api.get(`/projects/${activeProjectId}/engineering-schedule/stage-gate-configs`),
      ]);
      setEntries(entriesRes.data);
      setSummary(summaryRes.data);
      setDocumentTypes(docTypesRes.data);
      setStageGateConfigs(configsRes.data);
    } catch (error) {
      toast.error('Failed to load engineering schedule');
    } finally { setLoading(false); }
  }, [activeProjectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await api.post(`/projects/${activeProjectId}/engineering-schedule/sync-to-macro`);
      toast.success(`Synced to Macro Schedule: ${result.data.synced_activities || 0} activities updated`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally { setSyncing(false); }
  };

  const filteredEntries = entries.filter((entry) => {
    if (selectedDocType && entry.document_type_id !== selectedDocType) return false;
    if (statusFilter && entry.overall_status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!entry.document_number.toLowerCase().includes(q) && !entry.document_title.toLowerCase().includes(q) && !(entry.discipline || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <DashboardLayout>
      {ctxProjectId && <AIAnalystPanel projectId={ctxProjectId} moduleKey="ENGINEERING_SCHEDULE" defaultOpen={true} compact={true} />}

        <div className="flex items-center justify-center h-[600px]">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent)' }} />
        </div>
      </DashboardLayout>
    );
  }

  const completedCount = entries.filter((e) => e.overall_status === 'COMPLETED').length;
  const delayedCount = entries.filter((e) => e.overall_status === 'DELAYED' || e.overall_status === 'OVERDUE').length;
  const onTrackCount = entries.filter((e) => e.overall_status === 'ON_TRACK' || e.overall_status === 'AHEAD').length;

  return (
    <DashboardLayout>
      {ctxProjectId && <AIAnalystPanel projectId={ctxProjectId} moduleKey="ENGINEERING_SCHEDULE" defaultOpen={false} compact={true} />}
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/project-controls')}
            style={{ color: 'var(--text-muted)' }} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <ModuleHelp moduleKey="engineering" />
            <Button variant="outline" size="sm" onClick={() => setShowAutoLink(true)}
              data-testid="cognara-auto-link-macro-btn"
              style={{ color: '#7C3AED', borderColor: 'rgba(124,58,237,0.4)' }}>
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Cognara: Auto-Link Macro
            </Button>
            <PermissionGate module="DCI" action="create">
              <Button variant="outline" size="sm" onClick={() => setShowAddEntry(true)} data-testid="add-entry-btn"
                style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)' }}>
                <Plus className="w-4 h-4 mr-2" /> Add Entry
              </Button>
            </PermissionGate>
            <PermissionGate module="DCI" action="import">
              <Button variant="outline" size="sm" onClick={() => setShowExcelDialog(true)} data-testid="import-export-btn"
                style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)' }}>
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Import/Export
              </Button>
            </PermissionGate>
            <PermissionGate module="DCI" action="configure">
              <Button variant="outline" size="sm" onClick={() => setShowConfig(!showConfig)} data-testid="configure-btn"
                style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)' }}>
                <Settings className="w-4 h-4 mr-2" /> Configure
              </Button>
            </PermissionGate>
          </div>
        </div>

        {/* Title */}
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-[#143A6B]/20 to-[#143A6B]/10" style={{ border: '1px solid rgba(139, 92, 246, 0.3)' }}>
            <Wrench className="w-8 h-8 text-[#143A6B]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Engineering Schedule</h1>
            <p style={{ color: 'var(--text-muted)' }}>Document Control Index (DCI) - Track engineering deliverables through stage gates</p>
          </div>
        </div>

        {/* Data Flow Banner + Sync */}
        <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ backgroundColor: 'var(--status-on-track-bg)', border: '1px solid rgba(56,96,255,0.2)' }}>
                <ArrowDownLeft className="w-4 h-4" style={{ color: 'var(--accent-text)' }} />
                <span className="text-sm" style={{ color: 'var(--accent-text)' }}>Roll-down from Macro</span>
              </div>
              <span style={{ color: 'var(--text-disabled)' }}>|</span>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ backgroundColor: 'var(--status-completed-bg)', border: '1px solid rgba(16,185,129,0.2)' }}>
                <ArrowUpRight className="w-4 h-4" style={{ color: 'var(--status-completed-text)' }} />
                <span className="text-sm" style={{ color: 'var(--status-completed-text)' }}>Roll-up to Macro</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing} data-testid="sync-to-macro-btn"
                style={{ color: 'var(--status-completed-text)', borderColor: 'rgba(16,185,129,0.3)' }}>
                {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                Sync to Macro
              </Button>
              <Button variant="ghost" size="sm" data-testid="view-analytics-btn"
                onClick={() => navigate(`/project-controls/engineering/${activeProjectId}/analytics`)}
                style={{ color: '#143A6B' }}>
                <BarChart3 className="w-4 h-4 mr-2" /> S-Curve & Analytics
              </Button>
            </div>
          </div>
        </div>

        {/* Stage Gate Config */}
        {showAutoLink && (
          <CognaraMacroLinkModal projectId={activeProjectId}
            endpointPrefix="/eng" moduleLabel="Engineering"
            onClose={() => setShowAutoLink(false)}
            onCommitted={() => { setShowAutoLink(false); fetchData(); }} />
        )}
        <AnimatePresence>
          {showConfig && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                <StageGateConfig projectId={activeProjectId} onClose={() => { setShowConfig(false); fetchData(); }} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <KPICard title="Total Documents" value={summary?.total_documents || 0} icon={FileSpreadsheet} color="#1E4E8C" />
          <KPICard title="Completed" value={completedCount} subtitle={`${((completedCount / (summary?.total_documents || 1)) * 100).toFixed(0)}%`} icon={CheckCircle2} color="#10B981" />
          <KPICard title="On Track" value={onTrackCount} icon={TrendingUp} color="#6366F1" />
          <KPICard title="Delayed" value={delayedCount} subtitle={delayedCount > 0 ? 'Needs attention' : ''} icon={AlertTriangle} color="#EF4444" />
          <KPICard title="Overall Progress" value={`${(summary?.overall_actual_progress || 0).toFixed(1)}%`} subtitle={`Plan: ${(summary?.overall_planned_progress || 0).toFixed(1)}%`} icon={Clock} color="#143A6B" />
        </div>

        {/* Filters + View Toggle */}
        <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Search className="w-4 h-4" style={{ color: 'var(--text-disabled)' }} />
              <Input data-testid="search-input" placeholder="Search documents..." value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>
            <select data-testid="doc-type-filter" value={selectedDocType} onChange={(e) => setSelectedDocType(e.target.value)}
              className="px-3 py-2 rounded-md text-sm"
              style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
              <option value="">All Document Types</option>
              {documentTypes.map((dt) => <option key={dt.id} value={dt.id}>{dt.code} - {dt.name}</option>)}
            </select>
            <select data-testid="status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 rounded-md text-sm"
              style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
              <option value="">All Statuses</option>
              <option value="COMPLETED">Completed</option>
              <option value="ON_TRACK">On Track</option>
              <option value="AHEAD">Ahead</option>
              <option value="DELAYED">Delayed</option>
            </select>
            <Button variant="ghost" size="sm" onClick={() => { setSelectedDocType(''); setStatusFilter(''); setSearchQuery(''); }}
              style={{ color: 'var(--text-muted)' }} data-testid="reset-filters-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Reset
            </Button>

            {/* View Toggle */}
            <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)' }} data-testid="view-mode-toggle">
              <button onClick={() => setViewMode('register')} data-testid="view-register-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors"
                style={{
                  backgroundColor: viewMode === 'register' ? 'var(--accent)' : 'var(--card-bg)',
                  color: viewMode === 'register' ? '#ffffff' : 'var(--text-muted)',
                }}>
                <List className="w-3.5 h-3.5" /> Register
              </button>
              <button onClick={() => setViewMode('matrix')} data-testid="view-matrix-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors"
                style={{
                  backgroundColor: viewMode === 'matrix' ? 'var(--accent)' : 'var(--card-bg)',
                  color: viewMode === 'matrix' ? '#ffffff' : 'var(--text-muted)',
                  borderLeft: '1px solid var(--border-color)',
                }}>
                <Grid3X3 className="w-3.5 h-3.5" /> Matrix
              </button>
            </div>

            <div className="text-xs" style={{ color: 'var(--text-disabled)' }}>
              {filteredEntries.length} of {entries.length} documents
            </div>
          </div>
        </div>

        {/* DCI View */}
        {viewMode === 'register' ? (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                <List className="w-4 h-4" style={{ color: 'var(--accent-text)' }} />
                DCI Register
                <span style={{ color: 'var(--text-disabled)' }} className="font-normal">({filteredEntries.length} of {entries.length} documents)</span>
              </h3>
            </div>
            <DCIRegisterList entries={filteredEntries} projectId={activeProjectId} onRefresh={fetchData} />
          </div>
        ) : (
          <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <div className="p-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-color)' }}>
              <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                <Grid3X3 className="w-4 h-4" style={{ color: 'var(--accent-text)' }} />
                DCI Matrix
                <span style={{ color: 'var(--text-disabled)' }} className="font-normal">({filteredEntries.length} documents)</span>
              </h3>
              <Button variant="ghost" size="sm" onClick={fetchData} style={{ color: 'var(--text-muted)' }} data-testid="refresh-grid-btn">
                <RefreshCw className="w-4 h-4 mr-1" /> Refresh
              </Button>
            </div>
            <DCIMatrixGrid entries={filteredEntries} stageGateConfigs={stageGateConfigs} selectedDocType={selectedDocType}
              projectId={activeProjectId} onRefresh={fetchData} isDark={isDark} />
          </div>
        )}

        {/* Dialogs */}
        <GuidedImportWizard
          open={showExcelDialog}
          onOpenChange={setShowExcelDialog}
          projectId={activeProjectId}
          onImportComplete={fetchData}
          {...ENGINEERING_DCI_CONFIG(activeProjectId)}
        />
        <AnimatePresence>
          {showAddEntry && (
            <AddEntryDialog projectId={activeProjectId} documentTypes={documentTypes}
              onClose={() => setShowAddEntry(false)} onCreated={fetchData} />
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
